import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { AiSettingsGatewayPanel } from "../ai/AiSettingsGatewayPanel";
import { AiSettingsPanel } from "../ai/AiSettingsPanel";
import { AiGatewayClient } from "../ai/client";
import { DEFAULT_AI_RUNTIME_SETTINGS } from "../ai/settings";
import { clientEnvironment } from "../config/env";
import { featureFlags } from "../core/featureFlags";
import { HallPatrolLivePanel } from "../qr/HallPatrolLivePanel";
import { clearHallPatrolTankCache } from "../qr/tankSnapshotCache";
import { claimPlatformSubscriberSetup } from "../platform/subscriberManagement";
import { loadPlatformProfile } from "../platform/platformSubscriptions";
import { createOutboxStore } from "../sync/outboxFactory";
import { reconcileReadyOperations, syncPendingOperations } from "../sync/operationRuntime";
import { SupabaseSyncTransport } from "../sync/supabaseTransport";
import type { OutboxSummary } from "../sync/types";
import { AppShell, type AppShellSnapshot } from "../ux/AppShell";
import { BreedingInventoryPanel } from "../ux/BreedingInventoryPanel";
import { FeedingPanel } from "../ux/FeedingPanel";
import { FeedingPolicyAdministrationPanel } from "../ux/FeedingPolicyAdministrationPanel";
import { FinanceManagementPanel } from "../ux/FinanceManagementPanel";
import { FinanceWarehouseReportPanel } from "../ux/FinanceWarehouseReportPanel";
import { HallTankAdministrationPanel } from "../ux/HallTankAdministrationPanel";
import { ManagerAdministrationPanel } from "../ux/ManagerAdministrationPanel";
import { NotificationDeviceSettingsPanel } from "../ux/NotificationDeviceSettingsPanel";
import { OfflineCenterPanel } from "../ux/OfflineCenterPanel";
import { PlatformSubscriptionPanel } from "../ux/PlatformSubscriptionPanel";
import { SubscriberManagementPanel } from "../ux/SubscriberManagementPanel";
import { QrLabelPrintPanel } from "../ux/QrLabelPrintPanel";
import { ReportsDashboardPanel } from "../ux/ReportsDashboardPanel";
import { SystemVersionPanel } from "../ux/SystemVersionPanel";
import type { SettingsSurfaceId } from "../ux/SettingsHub";
import { TodayTaskCompletionPanel } from "../ux/TodayTaskCompletionPanel";
import { publicTaskTitle } from "../ux/taskDisplay";
import { TreatmentProtocolAdministrationPanel } from "../ux/TreatmentProtocolAdministrationPanel";
import { WarehouseAssetManagementPanel } from "../ux/WarehouseAssetManagementPanel";
import { WaterTopologyAdministrationPanel } from "../ux/WaterTopologyAdministrationPanel";
import { WorkerHallOperationsPanel } from "../ux/WorkerHallOperationsPanel";
import type { AppRole, ConnectivityState, WorkItem } from "../ux/model";
import { chooseFarmOption, type FarmOption } from "./farmSelection";
import type { PasswordSetupMode } from "./authCallbackIntent";
import { PasswordSetupScreen } from "./PasswordSetupScreen";
import {
  clearCachedShellSnapshot,
  readCachedShellSnapshot,
  writeCachedShellSnapshot,
} from "./snapshotCache";
import { SubscriberSetupClaimScreen } from "./SubscriberSetupClaimScreen";
import { initialPasswordSetupMode, supabase } from "./supabaseClient";

type BootState =
  | { kind: "loading" }
  | { kind: "signed_out"; error?: string; notice?: string }
  | {
      kind: "setup_claim";
      session: Session;
      userName: string;
    }
  | {
      kind: "farm_select";
      session: Session;
      userName: string;
      options: readonly FarmOption[];
    }
  | {
      kind: "ready";
      session: Session;
      farmId: string;
      farmOptions: readonly FarmOption[];
      snapshot: AppShellSnapshot;
    }
  | { kind: "error"; message: string };

interface IdentityContext {
  userName: string;
  farmOptions: FarmOption[];
}

function connectivityState(): ConnectivityState {
  return typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online";
}

function normalizeRole(value: unknown): AppRole {
  if (value === "owner" || value === "manager" || value === "operator") {
    return value;
  }
  throw new Error("C15_ROLE_UNSUPPORTED");
}

function workStatus(value: unknown, dueAt: unknown): WorkItem["status"] {
  if (value === "completed" || value === "cancelled") {
    return "done";
  }
  if (typeof dueAt === "string" && new Date(dueAt).getTime() < Date.now()) {
    return "late";
  }
  return "pending";
}

async function loadIdentityContext(userId: string): Promise<IdentityContext> {
  const profileResult = await supabase
    .from("user_profiles")
    .select("id,full_name,is_active")
    .eq("id", userId)
    .maybeSingle();

  if (profileResult.error) throw profileResult.error;
  if (!profileResult.data?.is_active) throw new Error("C15_PROFILE_INACTIVE_OR_MISSING");

  const membershipResult = await supabase
    .from("farm_memberships")
    .select("farm_id,role,is_active")
    .eq("user_id", userId)
    .eq("is_active", true);

  if (membershipResult.error) throw membershipResult.error;

  if (!membershipResult.data?.length) {
    return {
      userName: profileResult.data.full_name?.trim() || "کاربر",
      farmOptions: [],
    };
  }

  const farmIds = [
    ...new Set(membershipResult.data.map((membership) => String(membership.farm_id))),
  ];
  const farmsResult = await supabase.from("farms").select("id,name").in("id", farmIds);

  if (farmsResult.error) throw farmsResult.error;

  const farmNames = new Map(
    (farmsResult.data ?? []).map((farm) => [String(farm.id), String(farm.name)]),
  );

  const farmOptions = membershipResult.data.map((membership) => {
    const farmId = String(membership.farm_id);
    const farmName = farmNames.get(farmId);
    if (!farmName) {
      throw new Error("C15_FARM_NOT_FOUND");
    }

    return {
      farmId,
      farmName,
      role: normalizeRole(membership.role),
    } satisfies FarmOption;
  });

  return {
    userName: profileResult.data.full_name?.trim() || "کاربر",
    farmOptions,
  };
}

async function loadSnapshotForFarm(
  userId: string,
  userName: string,
  option: FarmOption,
): Promise<AppShellSnapshot> {
  const tasksResult = await supabase
    .from("hall_tasks")
    .select("id,tank_id,title,status,due_at,domain_version,assigned_to")
    .eq("farm_id", option.farmId)
    .neq("status", "completed")
    .order("due_at", { ascending: true, nullsFirst: false })
    .limit(50);

  if (tasksResult.error) throw tasksResult.error;

  const taskTankIds = [
    ...new Set(
      (tasksResult.data ?? [])
        .map((task) => (typeof task.tank_id === "string" ? task.tank_id : undefined))
        .filter((tankId): tankId is string => Boolean(tankId)),
    ),
  ];
  const tankCodeById = new Map<string, string>();

  if (taskTankIds.length > 0) {
    const tanksResult = await supabase
      .from("tanks")
      .select("id,tank_code")
      .eq("farm_id", option.farmId)
      .in("id", taskTankIds);

    if (tanksResult.error) throw tanksResult.error;

    for (const tank of tanksResult.data ?? []) {
      if (typeof tank.id === "string" && typeof tank.tank_code === "string") {
        tankCodeById.set(tank.id, tank.tank_code);
      }
    }
  }

  const workItems: WorkItem[] = (tasksResult.data ?? []).map((task) => {
    const tankCode = typeof task.tank_id === "string" ? tankCodeById.get(task.tank_id) : undefined;

    return {
      id: String(task.id),
      title: publicTaskTitle(task.title),
      kind: "task",
      status: workStatus(task.status, task.due_at),
      domainVersion: Number(task.domain_version),
      ...(tankCode ? { tankCode } : {}),
      canComplete:
        option.role !== "operator" ||
        task.assigned_to === null ||
        String(task.assigned_to) === userId,
      ...(typeof task.due_at === "string"
        ? {
            dueLabel: new Date(task.due_at).toLocaleTimeString("fa-IR", {
              hour: "2-digit",
              minute: "2-digit",
            }),
          }
        : {}),
    };
  });

  const platformProfile = await loadPlatformProfile(supabase).catch(() => ({
    isPlatformOwner: false,
  }));

  const outbox = await createOutboxStore();
  const scope = { farmId: option.farmId, actorId: userId };
  const sync = await (connectivityState() === "offline"
    ? outbox.summarize(scope)
    : reconcileReadyOperations(outbox, new SupabaseSyncTransport(supabase), scope).then(
        ({ summary }) => summary,
      )
  )
    .catch(() => outbox.summarize(scope))
    .catch(() => ({ pending: 0, syncing: 0, retryable: 0, conflicts: 0 }))
    .finally(() => outbox.close());

  return {
    role: option.role,
    userName,
    farmName: option.farmName,
    connectivity: connectivityState(),
    sync,
    workItems,
    hallPatrolActions: [],
    alerts: workItems.filter((item) => item.status === "late" || item.status === "conflict").length,
    isPlatformOwner: platformProfile.isPlatformOwner,
  };
}

function LoginScreen({
  error,
  notice,
  onSubmit,
  onSignUp,
  onPasswordReset,
}: {
  error?: string;
  notice?: string;
  onSubmit: (email: string, password: string) => Promise<void>;
  onSignUp: (email: string, password: string) => Promise<void>;
  onPasswordReset: (email: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [resetMessage, setResetMessage] = useState<string>();
  const [resetError, setResetError] = useState<string>();
  const [mode, setMode] = useState<"login" | "signup">("login");

  async function requestReset() {
    if (!email.trim()) {
      setResetError("ابتدا نشانی ایمیل را وارد کنید.");
      return;
    }
    setBusy(true);
    setResetError(undefined);
    setResetMessage(undefined);
    try {
      await onPasswordReset(email.trim());
      setResetMessage("اگر این ایمیل حساب فعال دارد، لینک بازیابی ارسال می‌شود.");
    } catch {
      setResetError("ارسال لینک بازیابی انجام نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = String(data.get("email") ?? "").trim();
    const password = String(data.get("password") ?? "");

    if (!email || !password) return;

    setBusy(true);
    try {
      if (mode === "login") {
        await onSubmit(email, password);
      } else {
        await onSignUp(email, password);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app-shell" dir="rtl">
      <section className="section-card" style={{ margin: "auto", width: "min(92vw, 420px)" }}>
        <p className="section-kicker">Discus AI V2</p>
        <h1>{mode === "login" ? "ورود به سالن" : "ساخت حساب مدیر جدید"}</h1>
        <p className="section-note">
          {mode === "signup"
            ? "بعد از ساخت حساب، کد راه‌اندازی سالن را وارد می‌کنید."
            : "با حساب موجود وارد شوید."}
        </p>
        <form className="operation-form" onSubmit={submit}>
          <label className="operation-field">
            ایمیل
            <input
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="operation-field">
            رمز عبور
            <input
              name="password"
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              required
            />
          </label>
          {notice ? <p className="operation-success">{notice}</p> : null}
          {error ? <p className="settings-warning">{error}</p> : null}
          {resetMessage ? <p className="operation-success">{resetMessage}</p> : null}
          {resetError ? <p className="settings-warning">{resetError}</p> : null}
          <button type="submit" className="primary-button large-button" disabled={busy}>
            {busy ? "در حال انجام…" : mode === "login" ? "ورود" : "ساخت حساب"}
          </button>
        </form>
        {mode === "login" ? (
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => void requestReset()}
          >
            رمز عبور را فراموش کرده‌اید؟
          </button>
        ) : null}
        <button
          type="button"
          className="text-button"
          disabled={busy}
          onClick={() => setMode((current) => (current === "login" ? "signup" : "login"))}
        >
          {mode === "login" ? "حساب جدید برای مشترک" : "بازگشت به ورود"}
        </button>
      </section>
    </main>
  );
}

function FarmSelectionScreen({
  userName,
  options,
  onSelect,
  onSignOut,
}: {
  userName: string;
  options: readonly FarmOption[];
  onSelect: (option: FarmOption) => Promise<void>;
  onSignOut: () => void;
}) {
  const [busyFarmId, setBusyFarmId] = useState<string>();
  const [error, setError] = useState<string>();

  return (
    <main className="app-shell" dir="rtl">
      <section className="section-card" style={{ margin: "auto", width: "min(92vw, 520px)" }}>
        <p className="section-kicker">Discus AI V2</p>
        <h1>انتخاب سالن</h1>
        <p className="section-note">{userName}، سالن فعال موردنظر را انتخاب کنید.</p>

        <div className="work-list">
          {options.map((option) => (
            <button
              type="button"
              className="work-item pending"
              key={option.farmId}
              disabled={Boolean(busyFarmId)}
              onClick={() => {
                setBusyFarmId(option.farmId);
                setError(undefined);
                void onSelect(option)
                  .catch(() => {
                    setError("بارگذاری سالن انتخاب‌شده انجام نشد.");
                  })
                  .finally(() => setBusyFarmId(undefined));
              }}
            >
              <span className="work-main">
                <strong>{option.farmName}</strong>
                <span>
                  {option.role === "owner" ? "مالک" : option.role === "manager" ? "مدیر" : "کارگر"}
                </span>
              </span>
              <span className="work-status">
                {busyFarmId === option.farmId ? "در حال ورود…" : "انتخاب"}
              </span>
            </button>
          ))}
        </div>

        {error ? <p className="settings-warning">{error}</p> : null}

        <button type="button" className="text-button" onClick={onSignOut}>
          خروج از حساب
        </button>
      </section>
    </main>
  );
}

export function LiveApp() {
  const [boot, setBoot] = useState<BootState>({ kind: "loading" });
  const passwordModeRef = useRef<PasswordSetupMode | null>(initialPasswordSetupMode);
  const [passwordMode, setPasswordMode] = useState<PasswordSetupMode | null>(
    initialPasswordSetupMode,
  );
  const [passwordSession, setPasswordSession] = useState<Session | null>(null);
  const lastAuthenticatedRef = useRef<{ userId: string; farmIds: Set<string> } | null>(null);

  const rememberFarm = useCallback((userId: string, farmId?: string) => {
    const current = lastAuthenticatedRef.current;
    const farmIds = current?.userId === userId ? new Set(current.farmIds) : new Set<string>();
    if (farmId) {
      farmIds.add(farmId);
    }
    lastAuthenticatedRef.current = { userId, farmIds };
  }, []);

  const updateSyncSummary = useCallback((summary: OutboxSummary) => {
    setBoot((current) =>
      current.kind === "ready"
        ? {
            ...current,
            snapshot: {
              ...current.snapshot,
              sync: summary,
            },
          }
        : current,
    );
  }, []);

  const updateTaskStatus = useCallback(
    (taskId: string, status: "done" | "conflict", summary: OutboxSummary) => {
      setBoot((current) => {
        if (current.kind !== "ready") return current;

        return {
          ...current,
          snapshot: {
            ...current.snapshot,
            sync: summary,
            workItems: current.snapshot.workItems.map((item) =>
              item.id === taskId ? { ...item, status } : item,
            ),
          },
        };
      });
    },
    [],
  );

  const hydrate = useCallback(
    async (session: Session | null) => {
      if (!session) {
        const previous = lastAuthenticatedRef.current;
        lastAuthenticatedRef.current = null;

        if (previous) {
          await clearCachedShellSnapshot(previous.userId).catch(() => undefined);
          await Promise.all(
            [...previous.farmIds].map((farmId) =>
              clearHallPatrolTankCache(farmId).catch(() => undefined),
            ),
          );
        }

        setBoot({ kind: "signed_out" });
        return;
      }

      const cached = await readCachedShellSnapshot(session.user.id).catch(() => undefined);
      rememberFarm(session.user.id, cached?.farmId);

      const livePlatformProfile =
        connectivityState() === "offline"
          ? undefined
          : await loadPlatformProfile(supabase).catch(() => undefined);

      if (cached) {
        setBoot({
          kind: "ready",
          session,
          farmId: cached.farmId,
          farmOptions: [
            {
              farmId: cached.farmId,
              farmName: cached.snapshot.farmName,
              role: cached.snapshot.role,
            },
          ],
          snapshot: {
            ...cached.snapshot,
            connectivity: connectivityState(),
            ...(livePlatformProfile
              ? { isPlatformOwner: livePlatformProfile.isPlatformOwner }
              : {}),
          },
        });
      }

      if (connectivityState() === "offline") {
        if (!cached) {
          setBoot({ kind: "error", message: "C15_OFFLINE_SNAPSHOT_MISSING" });
        }
        return;
      }

      try {
        const identity = await loadIdentityContext(session.user.id);

        if (identity.farmOptions.length === 0) {
          setBoot({
            kind: "setup_claim",
            session,
            userName: identity.userName,
          });
          return;
        }

        const selection = chooseFarmOption(identity.farmOptions, cached?.farmId);

        if (selection.kind === "selection_required") {
          setBoot({
            kind: "farm_select",
            session,
            userName: identity.userName,
            options: selection.options,
          });
          return;
        }

        const snapshot = await loadSnapshotForFarm(
          session.user.id,
          identity.userName,
          selection.option,
        );
        await writeCachedShellSnapshot(session.user.id, selection.option.farmId, snapshot).catch(
          () => undefined,
        );
        rememberFarm(session.user.id, selection.option.farmId);

        setBoot({
          kind: "ready",
          session,
          farmId: selection.option.farmId,
          farmOptions: identity.farmOptions,
          snapshot,
        });
      } catch (error) {
        if (cached) {
          setBoot({
            kind: "ready",
            session,
            farmId: cached.farmId,
            farmOptions: [
              {
                farmId: cached.farmId,
                farmName: cached.snapshot.farmName,
                role: cached.snapshot.role,
              },
            ],
            snapshot: {
              ...cached.snapshot,
              connectivity: "degraded",
            },
          });
          return;
        }

        setBoot({
          kind: "error",
          message: error instanceof Error ? error.message : "C15_BOOT_FAILED",
        });
      }
    },
    [rememberFarm],
  );

  const selectFarm = useCallback(
    async (
      session: Session,
      userName: string,
      farmOptions: readonly FarmOption[],
      option: FarmOption,
    ) => {
      const snapshot = await loadSnapshotForFarm(session.user.id, userName, option);
      await writeCachedShellSnapshot(session.user.id, option.farmId, snapshot);
      rememberFarm(session.user.id, option.farmId);

      setBoot({
        kind: "ready",
        session,
        farmId: option.farmId,
        farmOptions,
        snapshot,
      });
    },
    [rememberFarm],
  );

  useEffect(() => {
    if (boot.kind !== "ready") return;

    void writeCachedShellSnapshot(boot.session.user.id, boot.farmId, boot.snapshot).catch(
      () => undefined,
    );
  }, [boot]);

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setBoot({ kind: "signed_out", error: error.message });
        return;
      }
      if (passwordModeRef.current) {
        if (data.session) setPasswordSession(data.session);
        return;
      }
      void hydrate(data.session);
    });

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY") {
        passwordModeRef.current = "recovery";
        setPasswordMode("recovery");
        setPasswordSession(session);
        return;
      }
      if (passwordModeRef.current) {
        if (session) setPasswordSession(session);
        return;
      }
      void hydrate(session);
    });

    const updateConnectivity = () => {
      setBoot((current) =>
        current.kind === "ready"
          ? {
              ...current,
              snapshot: { ...current.snapshot, connectivity: connectivityState() },
            }
          : current,
      );
    };
    window.addEventListener("online", updateConnectivity);
    window.addEventListener("offline", updateConnectivity);

    return () => {
      active = false;
      data.subscription.unsubscribe();
      window.removeEventListener("online", updateConnectivity);
      window.removeEventListener("offline", updateConnectivity);
    };
  }, [hydrate]);

  const readyFarmId = boot.kind === "ready" ? boot.farmId : undefined;
  const readyActorId = boot.kind === "ready" ? boot.session.user.id : undefined;

  useEffect(() => {
    if (!readyFarmId || !readyActorId) return;

    const reconcileOnReconnect = () => {
      if (connectivityState() === "offline") return;

      void syncPendingOperations(supabase, {
        farmId: readyFarmId,
        actorId: readyActorId,
      })
        .then(({ summary }) => updateSyncSummary(summary))
        .catch(() => undefined);
    };

    window.addEventListener("online", reconcileOnReconnect);
    return () => window.removeEventListener("online", reconcileOnReconnect);
  }, [readyActorId, readyFarmId, updateSyncSummary]);

  const aiClient = useMemo(() => {
    if (boot.kind !== "ready" || !clientEnvironment.supabaseUrl) return undefined;

    return new AiGatewayClient({
      supabaseUrl: clientEnvironment.supabaseUrl,
      farmId: boot.farmId,
      getAccessToken: () => boot.session.access_token,
    });
  }, [boot]);

  if (passwordMode && passwordSession) {
    return (
      <PasswordSetupScreen
        mode={passwordMode}
        email={passwordSession.user.email}
        onSubmit={async (password) => {
          const { error } = await supabase.auth.updateUser({ password });
          if (error) throw error;
          passwordModeRef.current = null;
          setPasswordMode(null);
          setPasswordSession(null);
          const { data, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) throw sessionError;
          await hydrate(data.session);
        }}
        onCancel={async () => {
          passwordModeRef.current = null;
          setPasswordMode(null);
          setPasswordSession(null);
          await supabase.auth.signOut({ scope: "local" });
          setBoot({ kind: "signed_out" });
        }}
      />
    );
  }

  if (boot.kind === "loading") {
    return (
      <main className="app-shell" dir="rtl">
        <section className="section-card">در حال آماده‌سازی برنامه…</section>
      </main>
    );
  }

  if (boot.kind === "signed_out") {
    return (
      <LoginScreen
        {...(boot.error ? { error: boot.error } : {})}
        {...(boot.notice ? { notice: boot.notice } : {})}
        onSubmit={async (email, password) => {
          const { error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) {
            setBoot({ kind: "signed_out", error: "ورود ناموفق بود. اطلاعات ورود را بررسی کنید." });
          }
        }}
        onPasswordReset={async (email) => {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: clientEnvironment.authRedirectUrl,
          });
          if (error) throw error;
        }}
        onSignUp={async (email, password) => {
          const { data, error } = await supabase.auth.signUp({ email, password });
          if (error) {
            setBoot({
              kind: "signed_out",
              error: "ساخت حساب انجام نشد. ایمیل و رمز عبور را بررسی کنید.",
            });
            return;
          }

          if (data.session) {
            await hydrate(data.session);
            return;
          }

          setBoot({
            kind: "signed_out",
            notice: "حساب ساخته شد. اگر تأیید ایمیل فعال است، ایمیل را تأیید و سپس وارد شوید.",
          });
        }}
      />
    );
  }

  if (boot.kind === "setup_claim") {
    return (
      <SubscriberSetupClaimScreen
        userName={boot.userName}
        onClaim={async (setupCode) => {
          await claimPlatformSubscriberSetup(supabase, setupCode);
          await hydrate(boot.session);
        }}
        onSignOut={() => {
          void supabase.auth.signOut({ scope: "local" });
        }}
      />
    );
  }

  if (boot.kind === "farm_select") {
    return (
      <FarmSelectionScreen
        userName={boot.userName}
        options={boot.options}
        onSelect={(option) => selectFarm(boot.session, boot.userName, boot.options, option)}
        onSignOut={() => {
          void supabase.auth.signOut({ scope: "local" });
        }}
      />
    );
  }

  if (boot.kind === "error") {
    return (
      <main className="app-shell" dir="rtl">
        <section className="section-card">
          <h1>بارگذاری حساب کامل نشد</h1>
          <p className="settings-warning">{boot.message}</p>
          <button
            type="button"
            className="text-button"
            onClick={() => {
              void supabase.auth.signOut({ scope: "local" });
            }}
          >
            خروج از حساب
          </button>
        </section>
      </main>
    );
  }

  const hallPatrolPanel = (
    <HallPatrolLivePanel
      farmId={boot.farmId}
      userId={boot.session.user.id}
      items={boot.snapshot.workItems}
      onSyncSummary={updateSyncSummary}
      onTaskStatus={updateTaskStatus}
    />
  );
  const renderBreedingPanel =
    boot.snapshot.role === "operator"
      ? undefined
      : (tankId: string) => (
          <BreedingInventoryPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            selectedTankId={tankId}
            hideTankSelector
            onSyncSummary={updateSyncSummary}
          />
        );
  const renderSettingsPanel = (surface: SettingsSurfaceId) => {
    if (boot.snapshot.role === "operator") return null;

    switch (surface) {
      case "hall-structure":
        return null;
      case "tanks":
        return (
          <HallTankAdministrationPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            onSyncSummary={updateSyncSummary}
          />
        );
      case "water-circuits":
        return (
          <WaterTopologyAdministrationPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            onSyncSummary={updateSyncSummary}
          />
        );
      case "feeding-program":
        return (
          <FeedingPolicyAdministrationPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            onSyncSummary={updateSyncSummary}
          />
        );
      case "workers-permissions":
        return (
          <ManagerAdministrationPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            mode="permissions"
            onSyncSummary={updateSyncSummary}
          />
        );
      case "hall-plan":
        return <PlatformSubscriptionPanel />;
      case "notifications-devices":
        return (
          <NotificationDeviceSettingsPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            onSyncSummary={updateSyncSummary}
          />
        );
      case "qr-printing":
        return <QrLabelPrintPanel farmId={boot.farmId} />;
      case "ai":
        if (!featureFlags.aiUiEnabled) return null;
        return aiClient ? (
          <AiSettingsGatewayPanel client={aiClient} />
        ) : (
          <AiSettingsPanel
            value={DEFAULT_AI_RUNTIME_SETTINGS}
            backendReady={false}
            onSave={() => {
              throw new Error("AI_GATEWAY_NOT_READY");
            }}
            onTest={async () => "failed"}
          />
        );
      case "system-version":
        return (
          <SystemVersionPanel connectivity={boot.snapshot.connectivity} sync={boot.snapshot.sync} />
        );
      case "treatment-protocols":
        return (
          <TreatmentProtocolAdministrationPanel
            farmId={boot.farmId}
            userId={boot.session.user.id}
            onSyncSummary={updateSyncSummary}
          />
        );
    }
  };
  const managerSubscribersPanel =
    boot.snapshot.isPlatformOwner === true ? <SubscriberManagementPanel /> : undefined;
  const managerWorkersPanel =
    boot.snapshot.role === "operator" ? undefined : (
      <ManagerAdministrationPanel
        farmId={boot.farmId}
        userId={boot.session.user.id}
        mode="workers"
        onSyncSummary={updateSyncSummary}
      />
    );
  const managerReportsPanel =
    boot.snapshot.role === "operator" ? undefined : (
      <ReportsDashboardPanel farmId={boot.farmId} sync={boot.snapshot.sync} />
    );
  const managerFinancePanel =
    boot.snapshot.role === "operator" ? undefined : (
      <FinanceManagementPanel
        farmId={boot.farmId}
        userId={boot.session.user.id}
        onSyncSummary={updateSyncSummary}
      />
    );
  const managerFinanceWarehouseReportPanel =
    boot.snapshot.role === "operator" ? undefined : (
      <FinanceWarehouseReportPanel farmId={boot.farmId} />
    );
  const managerWarehouseAssetPanel =
    boot.snapshot.role === "operator" ? undefined : (
      <WarehouseAssetManagementPanel
        farmId={boot.farmId}
        userId={boot.session.user.id}
        onSyncSummary={updateSyncSummary}
      />
    );
  const workerOperationsPanel = (
    <WorkerHallOperationsPanel
      farmId={boot.farmId}
      userId={boot.session.user.id}
      {...(renderBreedingPanel ? { renderBreedingPanel } : {})}
      onSyncSummary={updateSyncSummary}
    />
  );
  const feedingPanel = (
    <FeedingPanel
      farmId={boot.farmId}
      userId={boot.session.user.id}
      onSyncSummary={updateSyncSummary}
    />
  );
  const todayTaskPanel = (
    <TodayTaskCompletionPanel
      farmId={boot.farmId}
      userId={boot.session.user.id}
      items={boot.snapshot.workItems}
      onSyncSummary={updateSyncSummary}
      onTaskStatus={updateTaskStatus}
    />
  );
  const offlineCenterPanel = (
    <OfflineCenterPanel
      client={supabase}
      farmId={boot.farmId}
      actorId={boot.session.user.id}
      onSyncSummary={updateSyncSummary}
    />
  );
  const changeFarm =
    boot.farmOptions.length > 1
      ? () =>
          setBoot({
            kind: "farm_select",
            session: boot.session,
            userName: boot.snapshot.userName,
            options: boot.farmOptions,
          })
      : undefined;
  const signOut = () => {
    void supabase.auth.signOut({ scope: "local" });
  };

  return aiClient ? (
    <AppShell
      key={`${boot.session.user.id}:${boot.farmId}:${boot.snapshot.role}`}
      snapshot={boot.snapshot}
      aiClient={aiClient}
      hallPatrolPanel={hallPatrolPanel}
      workerOperationsPanel={workerOperationsPanel}
      feedingPanel={feedingPanel}
      todayTaskPanel={todayTaskPanel}
      offlineCenterPanel={offlineCenterPanel}
      renderSettingsPanel={renderSettingsPanel}
      managerWorkersPanel={managerWorkersPanel}
      managerSubscribersPanel={managerSubscribersPanel}
      managerReportsPanel={managerReportsPanel}
      managerFinancePanel={managerFinancePanel}
      managerFinanceWarehouseReportPanel={managerFinanceWarehouseReportPanel}
      managerWarehouseAssetPanel={managerWarehouseAssetPanel}
      onSignOut={signOut}
      {...(changeFarm ? { onChangeFarm: changeFarm } : {})}
    />
  ) : (
    <AppShell
      key={`${boot.session.user.id}:${boot.farmId}:${boot.snapshot.role}`}
      snapshot={boot.snapshot}
      hallPatrolPanel={hallPatrolPanel}
      workerOperationsPanel={workerOperationsPanel}
      feedingPanel={feedingPanel}
      todayTaskPanel={todayTaskPanel}
      offlineCenterPanel={offlineCenterPanel}
      renderSettingsPanel={renderSettingsPanel}
      managerWorkersPanel={managerWorkersPanel}
      managerSubscribersPanel={managerSubscribersPanel}
      managerReportsPanel={managerReportsPanel}
      managerFinancePanel={managerFinancePanel}
      managerFinanceWarehouseReportPanel={managerFinanceWarehouseReportPanel}
      managerWarehouseAssetPanel={managerWarehouseAssetPanel}
      onSignOut={signOut}
      {...(changeFarm ? { onChangeFarm: changeFarm } : {})}
    />
  );
}
